import React, { useState, useEffect } from "react";
import { ArrowLeft, RefreshCw, Users, Mail, Phone, MapPin, Calendar, Loader2, Lock, Download, CreditCard, Hash, Copy, Check, FileText, UserPlus, Trash2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Checkbox } from "../components/ui/checkbox";
import { LOGO_URL } from "../data/mock";
import { InvoiceDrawer } from "../components/InvoiceDrawer";
import { AdminAnalytics } from "../components/AdminAnalytics";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "admin_token";

export default function AdminPage() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || null);
  const [password, setPassword] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState("");
  const [signups, setSignups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [drawerCustomer, setDrawerCustomer] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteCancelSub, setDeleteCancelSub] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const authenticated = Boolean(token);

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setSignups([]);
  };

  const openDeleteDialog = (customer) => {
    const hasActiveSub = customer.subscription_status === "active";
    setDeleteCancelSub(hasActiveSub); // default to true if active
    setDeleteTarget(customer);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await axios.delete(
        `${API}/admin/signup/${deleteTarget.id}`,
        {
          params: { cancel_subscription: deleteCancelSub },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const subMsg = res.data.cancelled_subscription_id
        ? " (Stripe subscription cancelled)"
        : "";
      toast.success(`Deleted ${deleteTarget.name}${subMsg}`);
      setSignups((prev) => prev.filter((s) => s.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not delete customer";
      toast.error(typeof msg === "string" ? msg : "Could not delete customer");
    } finally {
      setDeleting(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!password) return;
    setAuthLoading(true);
    setAuthError("");
    try {
      const res = await axios.post(`${API}/admin/verify`, { password });
      const t = res.data.token;
      sessionStorage.setItem(TOKEN_KEY, t);
      setToken(t);
    } catch (err) {
      setAuthError("Incorrect password. Please try again.");
    } finally {
      setAuthLoading(false);
    }
  };

  const fetchSignups = async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/admin/customers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSignups(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        logout();
        setError("Session expired. Please log in again.");
      } else {
        setError("Failed to load sign-ups. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authenticated) fetchSignups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-GB", {
      day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  };

  const statusBadge = (status) => {
    const styles = {
      paid: "bg-[#16C79A]/15 text-[#16C79A]",
      active: "bg-[#16C79A]/15 text-[#16C79A]",
      pending: "bg-amber-100 text-amber-700",
      unpaid: "bg-red-50 text-red-500",
      none: "bg-slate-100 text-slate-400",
      cancelled: "bg-red-50 text-red-500",
      incomplete: "bg-amber-100 text-amber-700",
    };
    const s = status || "none";
    return (
      <span className={`inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold ${styles[s] || styles.none}`}>
        {s === "none" ? "No payment" : s.charAt(0).toUpperCase() + s.slice(1)}
      </span>
    );
  };

  const copyReferral = async (signupId) => {
    const url = `${window.location.origin}/?ref=${signupId}#signup`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(signupId);
      toast.success("Referral link copied!");
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      toast.error("Could not copy link");
    }
  };

  const downloadCSV = () => {
    if (signups.length === 0) return;
    const headers = ["Name", "Email", "Address", "Phone", "Date", "Payment Status", "Subscription", "Payments Made", "Last Payment", "Referred By", "Referrals Made"];
    const rows = signups.map((s) => [
      s.name, s.email, s.address, s.phone,
      formatDate(s.created_at),
      s.payment_status || "none",
      s.subscription_status || "none",
      s.payments_count || 0,
      s.last_payment_date ? formatDate(s.last_payment_date) : "—",
      s.referred_by || "—",
      s.referral_count || 0,
    ]);
    const csvContent =
      [headers, ...rows].map((row) => row.map((val) => `"${val}"`).join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `steamers-signups-${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-[#f5fcf9] flex items-center justify-center px-6">
        <div className="w-full max-w-sm">
          <div className="bg-white rounded-3xl shadow-xl shadow-[#16C79A]/5 border border-[#e0f5ef] p-8 text-center">
            <div className="w-16 h-16 rounded-2xl bg-[#16C79A]/10 flex items-center justify-center mx-auto mb-6">
              <Lock size={28} className="text-[#16C79A]" />
            </div>
            <img src={LOGO_URL} alt="Steamers" className="h-16 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-[#0C2D48] mb-2">Admin Access</h2>
            <p className="text-sm text-slate-500 mb-6">Enter your password to view sign-ups</p>
            <form onSubmit={handleLogin} className="space-y-4">
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                data-testid="admin-password-input"
                className="h-12 rounded-xl text-center text-lg border-[#e0f5ef] focus:border-[#16C79A] focus:ring-[#16C79A]/20"
                autoFocus
              />
              {(authError || error) && (
                <p className="text-red-500 text-sm" data-testid="admin-auth-error">{authError || error}</p>
              )}
              <Button
                type="submit"
                disabled={authLoading || !password}
                data-testid="admin-login-btn"
                className="w-full bg-[#16C79A] hover:bg-[#0C2D48] text-white font-semibold py-3 rounded-xl transition-all duration-300 disabled:opacity-50"
              >
                {authLoading ? <Loader2 size={18} className="animate-spin mr-2" /> : null}
                {authLoading ? "Verifying..." : "Enter"}
              </Button>
            </form>
            <a href="/" className="inline-block mt-6 text-sm text-slate-400 hover:text-[#16C79A] transition-colors">
              &larr; Back to site
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f5fcf9]">
      <header className="bg-white border-b border-[#e0f5ef] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/" className="flex items-center gap-2 text-[#0C2D48] hover:text-[#16C79A] transition-colors font-medium text-sm">
              <ArrowLeft size={18} />
              Back to site
            </a>
            <div className="h-6 w-px bg-[#e0f5ef]" />
            <img src={LOGO_URL} alt="Steamers" className="h-10 w-auto" />
            <h1 className="text-xl font-bold text-[#0C2D48]">Sign-Ups</h1>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={downloadCSV}
              disabled={signups.length === 0}
              data-testid="admin-download-csv-btn"
              className="bg-[#16C79A] hover:bg-[#00b8bb] text-[#0C2D48] font-medium px-4 py-2 rounded-lg transition-all duration-300 disabled:opacity-50"
            >
              <Download size={16} className="mr-2" />
              Download CSV
            </Button>
            <Button
              onClick={fetchSignups}
              disabled={loading}
              data-testid="admin-refresh-btn"
              className="bg-[#16C79A] hover:bg-[#0C2D48] text-white font-medium px-4 py-2 rounded-lg transition-all duration-300"
            >
              <RefreshCw size={16} className={`mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button
              onClick={logout}
              data-testid="admin-logout-btn"
              variant="ghost"
              className="text-slate-500 hover:text-[#0C2D48]"
            >
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Analytics Dashboard */}
        <div className="mb-8">
          <AdminAnalytics token={token} refreshKey={signups.length} />
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-24">
            <Loader2 size={36} className="animate-spin text-[#16C79A] mb-4" />
            <p className="text-slate-500">Loading sign-ups...</p>
          </div>
        ) : error ? (
          <div className="text-center py-24">
            <p className="text-red-500 mb-4">{error}</p>
            <Button onClick={fetchSignups} className="bg-[#16C79A] text-white rounded-lg">
              Try Again
            </Button>
          </div>
        ) : signups.length === 0 ? (
          <div className="text-center py-24">
            <div className="w-20 h-20 rounded-full bg-[#e0f5ef] flex items-center justify-center mx-auto mb-6">
              <Users size={32} className="text-[#16C79A]" />
            </div>
            <h3 className="text-xl font-bold text-[#0C2D48] mb-2">No sign-ups yet</h3>
            <p className="text-slate-500">Sign-ups from the landing page will appear here.</p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block bg-white rounded-2xl shadow-sm border border-[#e0f5ef] overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#f0faf7] border-b border-[#e0f5ef]">
                    <th className="text-left px-3 py-4 text-xs font-bold uppercase tracking-wider text-[#0C2D48]/60">#</th>
                    <th className="text-left px-3 py-4 text-xs font-bold uppercase tracking-wider text-[#0C2D48]/60">Name</th>
                    <th className="text-left px-3 py-4 text-xs font-bold uppercase tracking-wider text-[#0C2D48]/60">Email</th>
                    <th className="text-left px-3 py-4 text-xs font-bold uppercase tracking-wider text-[#0C2D48]/60">Phone</th>
                    <th className="text-left px-3 py-4 text-xs font-bold uppercase tracking-wider text-[#0C2D48]/60">Signed Up</th>
                    <th className="text-left px-3 py-4 text-xs font-bold uppercase tracking-wider text-[#0C2D48]/60">Payment</th>
                    <th className="text-left px-3 py-4 text-xs font-bold uppercase tracking-wider text-[#0C2D48]/60">Sub</th>
                    <th className="text-left px-3 py-4 text-xs font-bold uppercase tracking-wider text-[#0C2D48]/60"># Paid</th>
                    <th className="text-left px-3 py-4 text-xs font-bold uppercase tracking-wider text-[#0C2D48]/60">Last Payment</th>
                    <th className="text-left px-3 py-4 text-xs font-bold uppercase tracking-wider text-[#0C2D48]/60">Referrals</th>
                    <th className="text-left px-3 py-4 text-xs font-bold uppercase tracking-wider text-[#0C2D48]/60">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {signups.map((s, i) => (
                    <tr
                      key={s.id}
                      data-testid={`admin-row-${i}`}
                      className="border-b border-[#f0faf7] last:border-0 hover:bg-[#f5fcf9] transition-colors"
                    >
                      <td className="px-3 py-3 text-sm text-slate-400 font-medium">{i + 1}</td>
                      <td className="px-3 py-3 text-sm font-semibold text-[#0C2D48]">
                        {s.name}
                        <div className="text-xs text-slate-400 font-normal mt-0.5 truncate max-w-[180px]" title={s.address}>{s.address}</div>
                      </td>
                      <td className="px-3 py-3 text-sm text-slate-600 break-all max-w-[180px]">{s.email}</td>
                      <td className="px-3 py-3 text-sm text-slate-600 whitespace-nowrap">{s.phone}</td>
                      <td className="px-3 py-3 text-sm text-slate-400 whitespace-nowrap">{formatDate(s.created_at)}</td>
                      <td className="px-3 py-3 text-sm" data-testid={`admin-payment-status-${i}`}>{statusBadge(s.payment_status)}</td>
                      <td className="px-3 py-3 text-sm">{statusBadge(s.subscription_status)}</td>
                      <td className="px-3 py-3 text-sm font-semibold text-[#0C2D48] text-center" data-testid={`admin-payments-count-${i}`}>{s.payments_count || 0}</td>
                      <td className="px-3 py-3 text-sm text-slate-500 whitespace-nowrap">{s.last_payment_date ? formatDate(s.last_payment_date) : "—"}</td>
                      <td className="px-3 py-3 text-sm text-center">
                        {s.referral_count > 0 ? (
                          <span className="inline-flex items-center gap-1 text-[#16C79A] font-semibold">
                            <UserPlus size={12} /> {s.referral_count}
                          </span>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-sm">
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => setDrawerCustomer(s)}
                            data-testid={`admin-invoices-btn-${i}`}
                            className="w-8 h-8 rounded-lg bg-[#f0faf7] hover:bg-[#16C79A]/20 text-[#16C79A] flex items-center justify-center transition-colors"
                            title="View invoices"
                          >
                            <FileText size={14} />
                          </button>
                          <button
                            onClick={() => copyReferral(s.id)}
                            data-testid={`admin-copy-referral-btn-${i}`}
                            className="w-8 h-8 rounded-lg bg-[#f0faf7] hover:bg-[#16C79A]/20 text-[#16C79A] flex items-center justify-center transition-colors"
                            title="Copy referral link"
                          >
                            {copiedId === s.id ? <Check size={14} /> : <Copy size={14} />}
                          </button>
                          <button
                            onClick={() => openDeleteDialog(s)}
                            data-testid={`admin-delete-btn-${i}`}
                            className="w-8 h-8 rounded-lg bg-red-50 hover:bg-red-100 text-red-500 flex items-center justify-center transition-colors"
                            title="Delete customer"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden space-y-4">
              {signups.map((s, i) => (
                <div
                  key={s.id}
                  data-testid={`admin-card-${i}`}
                  className="bg-white rounded-2xl p-5 shadow-sm border border-[#e0f5ef]"
                >
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-lg font-bold text-[#0C2D48]">{s.name}</span>
                    <span className="text-xs text-slate-400 bg-[#f0faf7] px-2 py-1 rounded-lg">#{i + 1}</span>
                  </div>
                  <div className="space-y-2.5">
                    <div className="flex items-center gap-2.5">
                      <Mail size={14} className="text-[#16C79A] flex-shrink-0" />
                      <span className="text-sm text-slate-600 break-all">{s.email}</span>
                    </div>
                    <div className="flex items-center gap-2.5">
                      <MapPin size={14} className="text-[#16C79A] flex-shrink-0" />
                      <span className="text-sm text-slate-600">{s.address}</span>
                    </div>
                    <div className="flex items-center gap-2.5">
                      <Phone size={14} className="text-[#16C79A] flex-shrink-0" />
                      <span className="text-sm text-slate-600">{s.phone}</span>
                    </div>
                    <div className="flex items-center gap-2.5">
                      <Calendar size={14} className="text-[#16C79A] flex-shrink-0" />
                      <span className="text-sm text-slate-400">{formatDate(s.created_at)}</span>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-[#f0faf7] grid grid-cols-2 gap-3">
                    <div className="flex items-start gap-2">
                      <CreditCard size={14} className="text-[#16C79A] mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mb-1">Payment</p>
                        {statusBadge(s.payment_status)}
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <RefreshCw size={14} className="text-[#16C79A] mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mb-1">Subscription</p>
                        {statusBadge(s.subscription_status)}
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <Hash size={14} className="text-[#16C79A] mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mb-1">Payments</p>
                        <p className="text-sm font-semibold text-[#0C2D48]">{s.payments_count || 0}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <UserPlus size={14} className="text-[#16C79A] mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mb-1">Referrals</p>
                        <p className="text-sm font-semibold text-[#0C2D48]">{s.referral_count || 0}</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 flex gap-2">
                    <Button
                      onClick={() => setDrawerCustomer(s)}
                      data-testid={`admin-invoices-btn-mobile-${i}`}
                      className="flex-1 bg-[#f0faf7] hover:bg-[#16C79A]/15 text-[#0C2D48] text-xs font-semibold rounded-lg py-2"
                    >
                      <FileText size={13} className="mr-1.5" />
                      Invoices
                    </Button>
                    <Button
                      onClick={() => copyReferral(s.id)}
                      data-testid={`admin-copy-referral-btn-mobile-${i}`}
                      className="flex-1 bg-[#f0faf7] hover:bg-[#16C79A]/15 text-[#0C2D48] text-xs font-semibold rounded-lg py-2"
                    >
                      {copiedId === s.id ? <Check size={13} className="mr-1.5" /> : <Copy size={13} className="mr-1.5" />}
                      {copiedId === s.id ? "Copied!" : "Referral link"}
                    </Button>
                    <Button
                      onClick={() => openDeleteDialog(s)}
                      data-testid={`admin-delete-btn-mobile-${i}`}
                      className="bg-red-50 hover:bg-red-100 text-red-500 text-xs font-semibold rounded-lg py-2 px-3"
                      title="Delete customer"
                    >
                      <Trash2 size={13} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {drawerCustomer && (
        <InvoiceDrawer
          customer={drawerCustomer}
          token={token}
          onClose={() => setDrawerCustomer(null)}
        />
      )}

      {/* Delete confirmation dialog */}
      <AlertDialog
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => !open && !deleting && setDeleteTarget(null)}
      >
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this customer?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove{" "}
              <span className="font-semibold text-[#0C2D48]">{deleteTarget?.name}</span>{" "}
              ({deleteTarget?.email}) and all their payment records from your dashboard.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>

          {deleteTarget?.subscription_status === "active" && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 my-2">
              <p className="text-sm font-semibold text-amber-900 mb-2">
                ⚠ This customer has an active 99 AED/month subscription.
              </p>
              <label className="flex items-start gap-2 cursor-pointer">
                <Checkbox
                  checked={deleteCancelSub}
                  onCheckedChange={(v) => setDeleteCancelSub(Boolean(v))}
                  data-testid="delete-cancel-sub-checkbox"
                  className="mt-0.5"
                />
                <span className="text-sm text-amber-900">
                  Also cancel their Stripe subscription (recommended — otherwise they
                  will keep being billed even after deletion).
                </span>
              </label>
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="delete-cancel-btn">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleting}
              data-testid="delete-confirm-btn"
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              {deleting ? (
                <>
                  <Loader2 size={14} className="animate-spin mr-2" />
                  Deleting…
                </>
              ) : (
                "Delete customer"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
